import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import api from "../lib/api";
import { StatusBadge, PriorityBadge } from "../components/badges";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUS_OPTS = ["new", "started", "in_process", "under_review", "approved", "cancelled"];

export default function Tasks() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchInput, setSearchInput] = useState(params.get("search") || "");

  const page = parseInt(params.get("page") || "1");
  const filters = {
    scope: params.get("scope") || "mine",
    status: params.get("status") || "",
    priority: params.get("priority") || "",
    department_id: params.get("department_id") || "",
    assigned_to: params.get("assigned_to") || "",
    created_by: params.get("created_by") || "",
    category: params.get("category") || "",
    overdue: params.get("overdue") || "",
    search: params.get("search") || "",
    sort: params.get("sort") || "created_at",
    order: params.get("order") || "desc",
  };

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    next.delete("page");
    setParams(next);
  };

  const load = useCallback(async () => {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && q.set(k, v));
    q.set("page", page);
    q.set("limit", 15);
    const res = await api.get(`/tasks?${q.toString()}`);
    setData(res.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const title = filters.scope === "team" ? "Team Tasks"
    : filters.status === "under_review" ? "Pending Review"
    : filters.overdue === "true" ? "Overdue Tasks" : "My Tasks";

  return (
    <div className="animate-fade-in space-y-5" data-testid="tasks-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Task Management</div>
          <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
        </div>
        <Button data-testid="tasks-create-button" onClick={() => navigate("/tasks/new")}
          className="bg-primary font-semibold hover:bg-[#1E3A8A]">+ Create Task</Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
          <form className="col-span-2" onSubmit={(e) => { e.preventDefault(); setFilter("search", searchInput); }}>
            <Input data-testid="task-search-input" placeholder="Search title..." value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)} />
          </form>
          <Select value={filters.status || "all"} onValueChange={(v) => setFilter("status", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="task-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.priority || "all"} onValueChange={(v) => setFilter("priority", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="task-filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              {["low", "medium", "high", "critical"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.department_id || "all"} onValueChange={(v) => setFilter("department_id", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="task-filter-department"><SelectValue placeholder="Department" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.assigned_to || "all"} onValueChange={(v) => setFilter("assigned_to", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="task-filter-assignee"><SelectValue placeholder="Assignee" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Assignees</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.category || "all"} onValueChange={(v) => setFilter("category", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="task-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.overdue || "all"} onValueChange={(v) => setFilter("overdue", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="task-filter-overdue"><SelectValue placeholder="Overdue" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="true">Overdue only</SelectItem>
              <SelectItem value="false">Not overdue</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" data-testid="task-filter-reset"
            onClick={() => { setParams({}); setSearchInput(""); }}>
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reset
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="sticky top-0 border-b border-slate-200 bg-slate-100/80 text-left text-xs uppercase tracking-wider text-slate-600">
                <th className="px-4 py-3 font-semibold">Task</th>
                <th className="px-4 py-3 font-semibold">Assigned To</th>
                <th className="px-4 py-3 font-semibold">Created By</th>
                <th className="px-4 py-3 font-semibold">Priority</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Due Date</th>
                <th className="px-4 py-3 font-semibold">Department</th>
              </tr>
            </thead>
            <tbody>
              {!data && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Loading...</td></tr>
              )}
              {data && data.items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400" data-testid="tasks-empty-state">No tasks match your filters.</td></tr>
              )}
              {data && data.items.map((t) => (
                <tr
                  key={t.id}
                  data-testid={`task-table-row-${t.id}`}
                  onClick={() => navigate(`/tasks/${t.id}`)}
                  className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50/80"
                >
                  <td className="max-w-xs px-4 py-3">
                    <div className="truncate font-semibold text-slate-800">{t.title}</div>
                    <div className="text-xs text-slate-500">{t.category}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{t.assigned_to_name}</td>
                  <td className="px-4 py-3 text-slate-500">{t.created_by_name}</td>
                  <td className="px-4 py-3"><PriorityBadge priority={t.priority} /></td>
                  <td className="px-4 py-3"><StatusBadge status={t.status} overdue={t.is_overdue} /></td>
                  <td className={`px-4 py-3 font-data text-xs ${t.is_overdue ? "font-bold text-rose-600" : "text-slate-600"}`}>
                    {t.due_date || "-"}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{t.department_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data && data.pages > 1 && (
          <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
            <span className="text-xs text-slate-500">{data.total} tasks - page {data.page} of {data.pages}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" data-testid="tasks-prev-page" disabled={page <= 1}
                onClick={() => { const n = new URLSearchParams(params); n.set("page", page - 1); setParams(n); }}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" data-testid="tasks-next-page" disabled={page >= data.pages}
                onClick={() => { const n = new URLSearchParams(params); n.set("page", page + 1); setParams(n); }}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
